/**
 * ===================================
 * COSTA CREATION - PERFORMANCE MONITOR
 * Lightweight performance tracking without affecting user experience
 * ===================================
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.isEnabled = window.Shopify?.analytics && !window.location.search.includes('preview_theme_id');
    
    if (this.isEnabled) {
      this.init();
    }
  }

  init() {
    // Track page load performance
    this.trackPageLoad();
    
    // Track Core Web Vitals
    this.trackCoreWebVitals();
    
    // Track CSS/JS loading
    this.trackResourceLoading();
  }

  trackPageLoad() {
    window.addEventListener('load', () => {
      // Use setTimeout to ensure all measurements are available
      setTimeout(() => {
        const navigation = performance.getEntriesByType('navigation')[0];
        
        if (navigation) {
          this.metrics.pageLoad = {
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
            loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
            totalTime: navigation.loadEventEnd - navigation.fetchStart
          };
        }
      }, 0);
    });
  }

  trackCoreWebVitals() {
    // Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.metrics.lcp = Math.round(lastEntry.startTime);
      });
      
      try {
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // Silent fail - don't affect user experience
      }

      // First Input Delay (FID)
      const fidObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry) => {
          this.metrics.fid = Math.round(entry.processingStart - entry.startTime);
        });
      });

      try {
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // Silent fail
      }

      // Cumulative Layout Shift (CLS)
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach((entry) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        this.metrics.cls = Math.round(clsValue * 10000) / 10000;
      });

      try {
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // Silent fail
      }
    }
  }

  trackResourceLoading() {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const resources = performance.getEntriesByType('resource');
        const cssResources = resources.filter(r => r.name.includes('.css'));
        const jsResources = resources.filter(r => r.name.includes('.js'));
        
        this.metrics.resources = {
          cssCount: cssResources.length,
          jsCount: jsResources.length,
          avgCssLoadTime: this.calculateAverageLoadTime(cssResources),
          avgJsLoadTime: this.calculateAverageLoadTime(jsResources)
        };
      }, 1000);
    });
  }

  calculateAverageLoadTime(resources) {
    if (resources.length === 0) return 0;
    
    const totalTime = resources.reduce((sum, resource) => {
      return sum + (resource.responseEnd - resource.fetchStart);
    }, 0);
    
    return Math.round(totalTime / resources.length);
  }

  // Log metrics to console in development (only when ?debug=performance is in URL)
  debug() {
    if (window.location.search.includes('debug=performance')) {
      console.group('🚀 Costa Creation Performance Metrics');
      console.table(this.metrics);
      console.groupEnd();
    }
  }

  // Get current metrics (for potential analytics integration)
  getMetrics() {
    return { ...this.metrics };
  }
}

// Initialize performance monitoring
document.addEventListener('DOMContentLoaded', () => {
  window.performanceMonitor = new PerformanceMonitor();
  
  // Debug after everything loads
  window.addEventListener('load', () => {
    setTimeout(() => {
      if (window.performanceMonitor) {
        window.performanceMonitor.debug();
      }
    }, 2000);
  });
});

// Export for potential use in other modules
window.PerformanceMonitor = PerformanceMonitor;